import { FrontendToolHandler } from '../types'
import { getActiveAgentBus, isAgentBusBound, type Fdc3LiteContext } from '../fdc3Bridge'

/** The intent an entitled factsheet app handles (Spec 010 §7). */
const VIEW_FACTSHEET_INTENT = 'Mandg.ViewFundFactsheet'

/**
 * Offered only when Ali is bound AND a workspace app actually handles the factsheet
 * intent, so the model never sees this verb when there is nothing to open.
 */
function isFactsheetAvailable(): boolean {
  if (!isAgentBusBound()) return false
  const intents = getActiveAgentBus()?.getSnapshot()?.availableIntents ?? []
  return intents.includes(VIEW_FACTSHEET_INTENT)
}

/**
 * `viewFactsheet` (Spec 010 §7) — open the fund factsheet for the workspace-selected
 * fund and read back its summary. A purpose-built wrapper over the
 * `Mandg.ViewFundFactsheet` intent so Ali never has to guess FDC3 intent names:
 * with no args it uses the selected fund already in `state.workspaceContext.latestContext`,
 * raises the intent, and returns the handler's `mandg.factsheetSummary` to summarise.
 *
 * Only offered when a workspace app handles the factsheet intent; a no-op off the bus.
 */
export const viewFactsheet: FrontendToolHandler = {
  enabled: isFactsheetAvailable,
  definition: {
    name: 'viewFactsheet',
    description:
      "Open a fund's factsheet and read its summary. Omit `fund` to use the fund already selected in the workspace (state.workspaceContext.latestContext).",
    parameters: {
      type: 'object',
      properties: {
        fund: {
          type: 'object',
          description:
            'Optional FDC3 fund/instrument context with a string `type`. Omit to use the selected fund.'
        }
      },
      required: []
    },
    requiresConfirmation: false
  },
  execute: async (args) => {
    const bus = getActiveAgentBus()
    if (!bus || !bus.isBound()) {
      return { success: false, message: 'Ali is not connected to a workspace bus.' }
    }
    const fund =
      args.fund && typeof args.fund === 'object' ? (args.fund as Fdc3LiteContext) : undefined
    const result = await bus.viewFactsheet(fund)
    return { success: result.ok, message: result.message, data: result.data }
  }
}
